import { TEXTOS } from '../../manual-textos';
import { itensValidos, ou } from '../../manual-utils';
import {
  blocoAlerta,
  blocoCapitulo,
  blocoImagem,
  blocoNota,
  blocoSecao,
  blocoTabela,
  blocoTexto,
} from '../blocos';
import type { Bloco } from '../blocos';
import type { ManualData } from '../../manual-types';

export function capitulo3(dados: ManualData): Bloco[] {
  const blocos: Bloco[] = [blocoCapitulo(3, 'Visão geral da máquina')];
  blocos.push(...blocoTexto(TEXTOS.aberturaVisaoGeral, 'c3-abertura'));

  blocos.push(blocoSecao('3.1', 'Descrição da máquina'));
  blocos.push(...(dados.descricao
    ? blocoTexto(dados.descricao, 'c3-descricao')
    : [blocoNota('Descrição funcional não informada.', 'c3-descricao-vazio')]));
  if (dados.principioFuncionamento) blocos.push(...blocoTexto(dados.principioFuncionamento, 'c3-principio'));
  // O texto do usuário pode já vir pontuado — não dobrar o ponto final.
  if (dados.aplicacao) {
    const aplicacao = dados.aplicacao.trim();
    const pontuado = /[.!?…]$/.test(aplicacao) ? aplicacao : `${aplicacao}.`;
    blocos.push(...blocoTexto(`Aplicação: ${pontuado}`, 'c3-aplicacao'));
  }

  const vistas = itensValidos(dados.vistas, ['titulo', 'legenda']);
  if (vistas.length) {
    blocos.push(blocoSecao('3.2', 'Apresentação e vistas'));
    vistas.forEach((vista, indice) => {
      blocos.push(...blocoImagem({
        id: `c3-vista-${indice}`,
        titulo: vista.titulo || `Vista ${indice + 1}`,
        imagem: vista.imagem,
        legenda: vista.legenda,
        chamadas: vista.chamadas,
        marcadores: vista.chamadas
          .filter((chamada) => typeof chamada.x === 'number' && typeof chamada.y === 'number')
          .map((chamada) => ({ numero: chamada.numero || '?', x: chamada.x as number, y: chamada.y as number, lx: chamada.lx, ly: chamada.ly })),
      }));
    });
  }

  blocos.push(blocoSecao(vistas.length ? '3.3' : '3.2', 'Especificações técnicas'));
  blocos.push(blocoAlerta({
    tipo: 'atencao',
    titulo: 'ATENÇÃO',
    texto: 'Respeite as especificações técnicas da máquina. A não observação destas especificações pode causar acidentes pessoais e danos ao equipamento.',
  }, 'c3-alerta-espec'));

  const gerais = [
    ['Tipo de processo', ou(dados.aplicacao), '–'],
    ['Modelo / tipo', ou(dados.modeloTipo), '–'],
    ['Fabricante', ou(dados.fabricante), '–'],
    ['Ano de fabricação', ou(dados.anoFabricacao), '–'],
    ['Capacidade', ou(dados.capacidade), '–'],
    ['Dimensões (L × A × P)', ou(dados.dimensoes), 'mm'],
    ['Peso da máquina', ou(dados.peso), 'kg'],
    ...itensValidos(dados.especGerais, ['item', 'valor']).map((item) => [ou(item.item, '—'), ou(item.valor, '—'), ou(item.unidade, '–')]),
  ];
  blocos.push(...blocoTabela({
    id: 'c3-espec-gerais',
    legenda: 'DADOS GERAIS',
    colunas: ['ITEM', 'ESPECIFICAÇÃO', 'UNIDADE'],
    linhas: gerais,
    larguras: ['46%', '38%', '16%'],
    alinharEsquerda: true,
    porPagina: 18,
  }));

  if (dados.opcoes.incluirEspecEletricas) {
    const eletricas = [
      ['Tensão de alimentação', ou(dados.tensao), 'V'],
      ...itensValidos(dados.especEletricas, ['item', 'valor']).map((item) => [ou(item.item, '—'), ou(item.valor, '—'), ou(item.unidade, '–')]),
    ];
    blocos.push(...blocoTabela({
      id: 'c3-espec-eletricas',
      legenda: 'DADOS ELÉTRICOS',
      colunas: ['ITEM', 'ESPECIFICAÇÃO', 'UNIDADE'],
      linhas: eletricas,
      larguras: ['46%', '38%', '16%'],
      alinharEsquerda: true,
      porPagina: 18,
    }));
  }

  if (dados.opcoes.incluirEspecHidraulicas) {
    blocos.push(...blocoTabela({
      id: 'c3-espec-hidraulicas',
      legenda: 'DADOS HIDRÁULICOS',
      colunas: ['ITEM', 'ESPECIFICAÇÃO', 'UNIDADE'],
      linhas: itensValidos(dados.especHidraulicas, ['item', 'valor']).map((item) => [ou(item.item, '—'), ou(item.valor, '—'), ou(item.unidade, '–')]),
      larguras: ['46%', '38%', '16%'],
      alinharEsquerda: true,
      porPagina: 18,
    }));
  }

  if (dados.opcoes.incluirEspecPneumaticas) {
    blocos.push(...blocoTabela({
      id: 'c3-espec-pneumaticas',
      legenda: 'DADOS PNEUMÁTICOS',
      colunas: ['ITEM', 'ESPECIFICAÇÃO', 'UNIDADE'],
      linhas: itensValidos(dados.especPneumaticas, ['item', 'valor']).map((item) => [ou(item.item, '—'), ou(item.valor, '—'), ou(item.unidade, '–')]),
      larguras: ['46%', '38%', '16%'],
      alinharEsquerda: true,
      porPagina: 18,
    }));
  }

  return blocos;
}
